// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { getUserProfile } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const logDebug = (message, data) => {
    console.log(`[AuthContext] ${message}`, data);
  };
  // Listen for authentication state changes.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          // Attempt to load the user's profile.
          const profile = await getUserProfile(user.uid);
          setUserProfile(profile);
        } catch (err) {
          console.error("Error loading user profile:", err);
          setError(err.message);
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    // Cleanup subscription on unmount.
    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const profile = await getUserProfile(user.uid);
      setUserProfile(profile);
      setCurrentUser(user);
    } catch (err) {
      console.error("Login error:", err);
      throw err;
    }
  };


  const register = async ({ name, email, password, role, title, school, district }) => {
    setLoading(true);
    setError(null);

    try {
      // Create a new user using Firebase Authentication.
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;

      await createUserProfile(user.uid, {
        name,
        email,
        role,
        title,
        school,
        district
      });

      return user;
    } catch (err) {
      console.error("Error during registration:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createUserProfile = async (userId, profileData) => {
    try {
      const userDocRef = doc(db, 'userProfiles', userId);
      await setDoc(userDocRef, {
        uid: userId,
        ...profileData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return await getUserProfile(userId);
    } catch (err) {
      console.error("Error creating user profile:", err);
      throw err;
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Optional: Check if profile exists, and if not, create one
      let existingProfile = null;
      try {
        existingProfile = await getUserProfile(user.uid);
      } catch (profileError) {
        if (profileError?.message !== "User not found") {
          throw profileError;
        }
      }

      if (!existingProfile) {
        await createUserProfile(user.uid, {
          name: user.displayName,
          email: user.email,
          role: 'teacher', // default, or ask user later
          title: '',
          school: '',
          district: ''
        });
      }

      setCurrentUser(user);
    } catch (err) {
      console.error("Google Sign-In error:", err);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserProfile(null);
    } catch (err) {
      console.error("Logout error:", err);
      throw err;
    }
  };

  const sendPasswordReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      console.error("Password reset error:", err);
      throw err;
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    login,
    logout,
    error,
    register,
    signInWithGoogle,
    sendPasswordReset 
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
